import React, { useState, useEffect } from 'react';
import { Smartphone, Check, AlertCircle } from 'lucide-react';
import { developers } from '../api';

/**
 * Phone number used as the SMS destination for login verification codes.
 *
 * Reads and writes /developer/me, so it works for developers and super admins
 * alike -- both session types carry a developer_id. Clearing the field removes
 * the number and leaves email as the only 2FA channel.
 */
const ProfilePhoneSettings = ({ initialPhone = null, initialSmsAvailable = true, onSaved }) => {
  const [phone, setPhone] = useState(initialPhone || '');
  const [savedPhone, setSavedPhone] = useState(initialPhone || '');
  const [smsAvailable, setSmsAvailable] = useState(initialSmsAvailable);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setPhone(initialPhone || '');
    setSavedPhone(initialPhone || '');
  }, [initialPhone]);

  useEffect(() => {
    setSmsAvailable(initialSmsAvailable);
  }, [initialSmsAvailable]);

  const isDirty = phone.trim() !== (savedPhone || '');

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await developers.updateMe({ phone_number: phone.trim() });
      const stored = response.data.phone_number || '';
      setPhone(stored);
      setSavedPhone(stored);
      setSmsAvailable(Boolean(response.data.sms_2fa_available));
      setSuccess(
        stored
          ? 'Phone number saved. You can now get login codes by text message.'
          : 'Phone number removed. Login codes will go to your email only.'
      );
      if (onSaved) onSaved(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save your phone number. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-xl p-6 shadow-lg shadow-primary/5">
      <h2 className="text-xl font-semibold text-foreground mb-1 flex items-center gap-2">
        <Smartphone className="w-5 h-5 text-primary" />
        Login Verification by Text
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Add a mobile number to receive your two-factor login code by SMS instead of email.
        You choose which one to use each time you sign in.
      </p>

      {savedPhone && !smsAvailable && (
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-500 px-4 py-3 rounded-lg text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            Your number is saved, but SMS delivery is not switched on for this server yet.
            Codes will keep going to your email until it is configured.
          </span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="profile-phone" className="text-sm font-medium text-foreground">
            Mobile number
          </label>
          <input
            id="profile-phone"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
              setError('');
              setSuccess('');
            }}
            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            placeholder="+1 555 123 4567"
            disabled={saving}
            autoComplete="tel"
          />
          <p className="text-xs text-muted-foreground">
            Use international format. A 10-digit US number is accepted and saved as +1.
            Leave blank to remove the number.
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 bg-green-500/10 border border-green-500/20 text-green-500 px-4 py-3 rounded-lg text-sm">
            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving || !isDirty}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Phone Number'}
          </button>
          {isDirty && !saving && (
            <button
              type="button"
              onClick={() => {
                setPhone(savedPhone || '');
                setError('');
                setSuccess('');
              }}
              className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ProfilePhoneSettings;
